// Phase 1 site terminology is English-only; the override endpoints still
// require an explicit locale.
export const TERM_LOCALE = "en";

export const ALL_CATEGORIES = "__all__";

export const SHOW_FILTERS = {
	ALL: "All",
	OVERRIDDEN: "Overridden",
	DEFAULT: "Default",
	RECENT: "Recently edited",
};

export const categoryOf = (key) => key.split(".")[0];

// "entity.taskListNo" -> "Task List No", "entity.customField1" -> "Custom Field 1"
export const labelize = (key) => {
	const last = key.split(".").pop() ?? "";

	return last
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/([a-zA-Z])(\d)/g, "$1 $2")
		.replace(/^./, (c) => c.toUpperCase());
};

// entity.* terms map onto the legacy CustomCaption DTO properties that the
// rest of the app still reads from `me.customCaptions` (entity.asset ->
// asset / assetPlural). Non-entity keys have no legacy counterpart.
export const ccPropertyForKey = (key) =>
	key.startsWith("entity.") ? key.slice("entity.".length) : null;

// Joins the keys endpoint ([{ key, defaults }]) with the override rows
// ([{ key, locale, pluralCategory, value }]) into one editable term model per
// key. Overrides store non-plural values under a null pluralCategory, which
// the upsert endpoint addresses with the "" form key.
export const buildTerms = (keys = [], overrides = []) => {
	const overrideForms = new Map();

	overrides.forEach((row) => {
		const forms = overrideForms.get(row.key) ?? {};
		forms[row.pluralCategory ?? ""] = row.value;
		overrideForms.set(row.key, forms);
	});

	return keys.map(({ key, defaults = [] }) => {
		const forms = overrideForms.get(key) ?? {};
		const hasPlural = defaults.length > 1;
		const defaultSingular = defaults[0] ?? "";
		const defaultPlural = hasPlural ? defaults[1] : "";
		const overrideSingular =
			(hasPlural ? forms.one ?? forms[""] : forms[""] ?? forms.one) ?? null;
		const overridePlural = forms.other ?? null;
		const label = labelize(key);
		const effectiveSingular = overrideSingular ?? defaultSingular;
		const effectivePlural = overridePlural ?? defaultPlural;

		return {
			key,
			category: categoryOf(key),
			label,
			hasPlural,
			defaultSingular,
			defaultPlural,
			overrideSingular,
			overridePlural,
			isOverridden: Object.keys(forms).length > 0,
			effectiveSingular,
			effectivePlural,
			searchText: [
				key,
				label,
				defaultSingular,
				defaultPlural,
				effectiveSingular,
				effectivePlural,
			]
				.filter(Boolean)
				.join(" ")
				.toLowerCase(),
		};
	});
};

// Substring matches rank first (earlier hit = better); otherwise an ordered
// subsequence match scores by run length. -1 means no match.
export const fuzzyScore = (query, text) => {
	const q = query.toLowerCase();
	const s = text.toLowerCase();

	if (!q) return 0;

	const idx = s.indexOf(q);
	if (idx >= 0) return 1000 - idx;

	let from = 0;
	let score = 0;
	let run = 0;

	for (const ch of q) {
		const found = s.indexOf(ch, from);
		if (found === -1) return -1;

		run = found === from ? run + 1 : 1;
		score += run;
		from = found + 1;
	}

	return score;
};

export const filterTerms = (
	terms,
	{
		query = "",
		category = ALL_CATEGORIES,
		show = SHOW_FILTERS.ALL,
		recentKeys = {},
	} = {}
) => {
	let list = terms;

	if (category !== ALL_CATEGORIES) {
		list = list.filter((t) => t.category === category);
	}

	if (show === SHOW_FILTERS.OVERRIDDEN) {
		list = list.filter((t) => t.isOverridden);
	} else if (show === SHOW_FILTERS.DEFAULT) {
		list = list.filter((t) => !t.isOverridden);
	} else if (show === SHOW_FILTERS.RECENT) {
		list = list
			.filter((t) => recentKeys[t.key])
			.sort((a, b) => recentKeys[b.key] - recentKeys[a.key]);
	}

	const q = query.trim();
	if (!q) return list;

	return list
		.map((t) => ({ term: t, score: fuzzyScore(q, t.searchText) }))
		.filter((x) => x.score >= 0)
		.sort((a, b) => b.score - a.score)
		.map((x) => x.term);
};

// Keeps the legacy `me.customCaptions` blob (read synchronously all over the
// app) in step with a saved or reset entity.* term — the same in-place
// storage update the old Custom Captions screen performed.
export const syncCustomCaptionStorage = (term, { singular, plural }) => {
	const prop = ccPropertyForKey(term.key);
	if (!prop) return;

	const stored =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	if (!stored?.customCaptions) return;

	const updated = {
		...stored,
		customCaptions: {
			...stored.customCaptions,
			[prop]: singular,
			...(term.hasPlural ? { [`${prop}Plural`]: plural } : {}),
		},
	};

	localStorage.setItem("me", JSON.stringify(updated));
	sessionStorage.setItem("me", JSON.stringify(updated));
};
